import {
  ChangeDetectionStrategy,
  Component, computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClientSessionService, ClientSubjectData, UiSession } from '@brighthub/uisession';
import { Avatar } from 'primeng/avatar';
import { ButtonDirective } from 'primeng/button';
import {
  trigger,
  state,
  style,
  animate,
  transition,
  keyframes
} from '@angular/animations';
import { MenuItem, MessageService } from 'primeng/api';
import { CapitalizeFirstPipe } from '@brighthub/helpers';
import { Tooltip } from 'primeng/tooltip';
import { FeedbackConfig, FeedbackDialogComponent } from '@brighthub/feedback';

@Component({
  selector: 'bai-client-info',
  standalone: true,
  imports: [
    CommonModule,
    Avatar,
    ButtonDirective,
    CapitalizeFirstPipe,
    Tooltip,
    FeedbackDialogComponent,
  ],
  styleUrls: ['./client-info.component.scss'],
  templateUrl: './client-info.component.html',
  animations: [
    trigger('highlightPulse', [
      state('inactive', style({})),
      state('active', style({})),
      transition('inactive => active', [
        animate(
          '3s ease-in-out',
          keyframes([
            style({ boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)', offset: 0 }),
            style({
              boxShadow: '0 0 0 8px rgba(59, 130, 246, 0.4)',
              offset: 0.5,
            }),
            style({ boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)', offset: 1 }),
          ])
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientInfoComponent {
  readonly _clientSessionService = inject(ClientSessionService);
  readonly _messageService = inject(MessageService);
  readonly _toast: MessageService = inject(MessageService);

  @Input({ required: true }) session!: UiSession;
  @Input() selected = false;
  @Input() lastMessage = '';
  @Input() timestamp = '';
  @Input() highlight: boolean | undefined = false;
  @Input() showArchiveButton = false;
  @Input() showGuid = true;
  @Output() archiveSession = new EventEmitter<void>();
  @Input() archived = false;
  @ViewChild('menu') menu: any;
  @Input() collapsed = false;
  readonly _activeSession = computed(() =>
    this.archived
      ? this._clientSessionService.findActiveSessionForSubject(
          this.session.subjectId
        )
      : null
  );
  get initials(): string {
    const { firstName, lastName } = this.session.subjectData;
    return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
  }
  showFeedbackDialog = false;
  archivedChatFeedback: FeedbackConfig = {
    scope: { application: 'brightai', component: 'archived-chat' },
    titleText: 'How was your overall experience?',
    commentText: 'Share any thoughts before you close this chat.',
    suggestedTags: [
      'Helpful',
      'Accurate',
      'Appreciated',
      'Confusing',
      'Wrong',
      'Too Slow',
    ],
  };

  private readonly _feedbackSkipResponses: string[] = [
    "Skipping feedback this time? No worries — your thoughts next time could help shape a smoother experience for everyone.",
    "Most people share quick feedback after chats — yours could make your next one even better.",
    "Every voice counts. When you’re ready, your input will make a difference.",
    "Feedback only takes seconds, but its impact lasts way longer.",
    "You’re part of shaping this — next time, add your voice to the mix.",
    "Others are helping us improve with feedback. Join them next time?",
    "We get better when you weigh in. Hope you’ll share your take next round.",
    "Feedback is the fastest way to make your conversations feel effortless.",
    "A quick thought from you today can make tomorrow’s experience smoother.",
    "Skipping is fine — but your future self will thank you for leaving feedback."
  ];


  ngOnInit() {
    this.archivedChatFeedback['metadata'] = {
      conversationId: this.session.conversationId,
    }
  }

  handleArchiveSession($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.showFeedbackDialog = true;
  }

  selectClient() {
    this._clientSessionService.createOrSelectClientConversation(this.session);
  }

  handleActivateOrCreateSession($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    this._clientSessionService.setArchivedView(false);
    if (this._activeSession()) {
      this._clientSessionService.createOrSelectClientConversation(
        this._activeSession()!
      );
    } else {
      this._messageService.add({
        severity: 'info',
        summary: 'Creating new conversation',
        detail: `Creating a new conversation for ${this.session.subjectData.firstName} ${this.session.subjectData.lastName}`,
      });
      this._clientSessionService.createOrSelectClientConversation(
        this.session.subjectData.guid
      );
    }
  }

  closeArchiveFeedback() {
    this.showFeedbackDialog = false;
    this._clientSessionService.archiveClientSession(this.session.uiSessionId);
  }

  private _getRandomSkipMessage(): string {
    const i = Math.floor(Math.random() * this._feedbackSkipResponses.length);
    return this._feedbackSkipResponses[i];
  }

  cancelArchiveFeedback() {
    this._toast.add({
      severity: 'warn',
      detail: this._getRandomSkipMessage(),
    });
    this.closeArchiveFeedback();
  }
}
